/**
 * EmptyState — a centred placeholder for empty lists / first-run screens. An
 * icon in a soft rose disc, a serif title, a muted description, and an optional
 * action slot (typically a Button).
 */
import { forwardRef, type HTMLAttributes, type ReactNode } from "react";

export interface EmptyStateProps extends Omit<HTMLAttributes<HTMLDivElement>, "title"> {
  /** Icon element (a lucide glyph), shown in a soft rose disc. */
  readonly icon?: ReactNode;
  /** Title line (serif). */
  readonly title: ReactNode;
  /** Supporting description. */
  readonly description?: ReactNode;
  /** Optional action (e.g. a Button). */
  readonly action?: ReactNode;
}

export const EmptyState = forwardRef<HTMLDivElement, EmptyStateProps>(
  function EmptyState({ icon, title, description, action, className, ...rest }, ref) {
    return (
      <div
        ref={ref}
        className={["lc-empty", className].filter(Boolean).join(" ")}
        {...rest}
      >
        {icon != null ? (
          <span className="lc-empty__icon" aria-hidden="true">
            {icon}
          </span>
        ) : null}
        <h3 className="lc-empty__title">{title}</h3>
        {description != null ? (
          <p className="lc-empty__description">{description}</p>
        ) : null}
        {action != null ? <div className="lc-empty__action">{action}</div> : null}
      </div>
    );
  },
);
